import React from 'react';
import { Eye, Plus, Phone, Mail, UserCheck, UserX, Clock, FileText, Send, Download } from 'lucide-react';
import { Badge, Avatar, TableActionButton } from "../../../../components/ui";

const fmtDate = (d) => d ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

export const DesignGridView = ({ data, isLoading, onDesignClick, onEditClick, onToggleDisable, onRequestApproval }) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-100 p-4 animate-pulse min-w-0">
            <div className="flex gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-slate-100" />
              <div className="flex-1 space-y-2">
                <div className="h-3 bg-slate-100 rounded w-1/4" />
                <div className="h-4 bg-slate-100 rounded w-3/4" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="py-16 text-center text-slate-400 font-bold text-xs tracking-wide">
        No designs found
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 min-w-0">
      {data.map((design) => {
        const off = design.isDisabled;
        const fullName = design.customerName || `${design.firstName || ""} ${design.lastName || ""}`.trim() || "No Name";
        
        let displayNote = design.lastNote || design.notes || "No notes yet";
        if (displayNote && typeof displayNote === 'string' && displayNote.includes(' | Note: ')) {
          displayNote = displayNote.split(' | Note: ')[1].trim();
        }

        return (
          <div
            key={design._id}
            className={`group bg-white rounded-xl border border-slate-200/80 p-5 transition-all duration-300 hover:shadow-md hover:border-slate-300 flex flex-col gap-4 ${
              off ? 'opacity-85 grayscale-[0.2]' : ''
            }`}
          >
            {/* Top Row: Avatar + Identity + Status */}
            <div className="flex items-start justify-between gap-2 min-w-0">
              <div className="flex items-center gap-2.5 min-w-0">
                <Avatar 
                  name={fullName} 
                  className={`w-10 h-10 text-xs rounded-full flex items-center justify-center font-bold flex-shrink-0 shadow-sm border ${
                    off ? 'bg-slate-500 text-white border-slate-600/60' : 'bg-slate-900 text-white border-slate-700/60'
                  }`}
                />
                <div className="min-w-0">
                  <div className="text-[9px] font-extrabold text-slate-400 uppercase tracking-widest leading-none">
                    #{design.customId?.toUpperCase() || 'VERVE-ID'}
                  </div>
                  <h3 className="text-[13px] font-bold text-slate-800 truncate leading-tight mt-1.5 font-serif">
                    {fullName}
                  </h3>
                </div>
              </div>
              
              <div className="flex-shrink-0">
                {(() => {
                  const approval = design.designApproval || {};
                  if (!approval.isRequested) return <Badge variant="secondary" className="text-[9px] px-2 py-0.5">Not Requested</Badge>;
                  
                  const { designer, mainDesigner } = approval;
                  if (mainDesigner?.status === 'approved') return <Badge variant="success" className="text-[9px] px-2 py-0.5">Fully Approved</Badge>;
                  if (mainDesigner?.status === 'rejected' || designer?.status === 'rejected') return <Badge variant="danger" className="text-[9px] px-2 py-0.5">Rejected</Badge>;
                  if (designer?.status === 'approved') return <Badge variant="primary" className="text-[9px] px-2 py-0.5">Pending Main</Badge>;
                  
                  return <Badge variant="primary" className="text-[9px] px-2 py-0.5">Requested</Badge>;
                })()}
              </div>
            </div>

            {/* Contact Info: Two Column Grid */}
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-slate-50/60 border border-slate-100 rounded-lg p-2.5 flex flex-col gap-1 min-w-0">
                <div className="flex items-center gap-1 text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                  <Phone size={10} className="text-slate-400 shrink-0" />
                  <span>Phone</span>
                </div>
                <div className="text-[11px] font-semibold text-slate-700 truncate leading-none">
                  {design.phoneNumber || design.phone || '—'}
                </div>
              </div>
              <div className="bg-slate-50/60 border border-slate-100 rounded-lg p-2.5 flex flex-col gap-1 min-w-0">
                <div className="flex items-center gap-1 text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                  <Mail size={10} className="text-slate-400 shrink-0" />
                  <span>Email</span>
                </div>
                <div className="text-[11px] font-semibold text-slate-700 truncate leading-none">
                  {design.email ? `${design.email.split('@')[0]}@...` : '—'}
                </div>
              </div>
            </div>

            {/* Details Section: List Style */}
            <div className="flex flex-col gap-2 pt-1">
              <div className="flex items-center justify-between gap-3 text-[11px]">
                <div className="flex items-center gap-1.5 text-slate-400 font-bold uppercase tracking-wider text-[9px]">
                  <FileText size={12} className="shrink-0" />
                  <span>Last Note</span>
                </div>
                <span className="font-medium italic text-slate-600 truncate max-w-[150px]">
                  {displayNote}
                </span>
              </div>
              
              <div className="border-t border-slate-100 my-1 w-full" />

              <div className="flex items-center justify-between gap-3 text-[11px]">
                <div className="flex items-center gap-1.5 text-slate-400 font-bold uppercase tracking-wider text-[9px]">
                  <Clock size={12} className="shrink-0" />
                  <span>Updated At</span>
                </div>
                <span className="font-semibold text-slate-700 truncate">
                  {fmtDate(design.updatedAt)}
                </span>
              </div>
            </div>

            {/* Footer: Right Aligned Actions */}
            <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100 bg-slate-50/50 -mx-5 -mb-5 px-5 py-3 rounded-b-xl">
              {(() => {
                const approval = design.designApproval || {};
                const isRequested = approval.isRequested;
                const isRejected = approval.designer?.status === 'rejected' || approval.mainDesigner?.status === 'rejected';
                const isEditDisabled = isRequested && !isRejected;

                return (
                  <>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onDesignClick && onDesignClick(design); }}
                      className="w-8 h-8 rounded-lg bg-white border border-slate-200 text-[#2b448f] hover:text-white hover:bg-[#2b448f] hover:border-[#2b448f] flex items-center justify-center transition-all duration-200 hover:shadow-sm cursor-pointer active:scale-95 shadow-[0_2px_4px_rgba(0,0,0,0.02)]"
                      title="View Design Details"
                    >
                      <Eye size={13} />
                    </button>

                    <button
                      type="button"
                      disabled={isEditDisabled}
                      onClick={(e) => { e.stopPropagation(); if (!isEditDisabled && onEditClick) onEditClick(design); }}
                      className={`w-8 h-8 rounded-lg border flex items-center justify-center transition-all duration-200 hover:shadow-md active:scale-95 shadow-[0_2px_4px_rgba(0,0,0,0.02)] ${
                        isEditDisabled 
                          ? 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed'
                          : 'bg-white border-slate-200 text-orange-600 hover:text-white hover:bg-orange-500 hover:border-orange-500 cursor-pointer'
                      }`}
                      title={isEditDisabled ? "Cannot edit while approval is pending" : "Add Media"}
                    >
                      <Plus size={13} />
                    </button>

                    <button
                      type="button"
                      disabled={isEditDisabled}
                      onClick={(e) => { e.stopPropagation(); if (!isEditDisabled && onToggleDisable) onToggleDisable(design); }}
                      className={`w-8 h-8 rounded-lg border flex items-center justify-center transition-all duration-200 hover:shadow-md active:scale-95 shadow-[0_2px_4px_rgba(0,0,0,0.02)] ${
                        isEditDisabled
                          ? 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed'
                          : off 
                            ? 'bg-white border-slate-200 text-emerald-600 hover:text-white hover:bg-emerald-500 hover:border-emerald-500 cursor-pointer' 
                            : 'bg-white border-slate-200 text-rose-500 hover:text-white hover:bg-rose-500 hover:border-rose-500 cursor-pointer'
                      }`}
                      title={isEditDisabled ? "Disabled during approval" : (off ? 'Enable Design' : 'Disable Design')}
                    >
                      {off ? <UserCheck size={13} /> : <UserX size={13} />}
                    </button>

                    <TableActionButton
                      icon={Download}
                      variant={isFullyApproved ? "success" : "neutral"}
                      disabled={!isFullyApproved}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isFullyApproved) {
                          if (design.designMedia && design.designMedia.length > 0) {
                            design.designMedia.forEach(media => {
                              window.open(media.url, '_blank');
                            });
                          } else {
                            alert("No design media files found to download.");
                          }
                        }
                      }}
                      title={isFullyApproved ? "Download Design Media" : "Download available only after full approval"}
                    />
                  </>
                );
              })()}
            </div>
          </div>
        );
      })}
    </div>
  );
};